<template>
  <div class="field-group">

    <div class="field-group-item">
      <span>{{ typesLabel }}</span>
      <div class="creative-type-list">
        <label v-for="opt in creativeTypeOptions" :key="opt.value" class="creative-type-item"
          :class="{ disabled: !isCreativeAvailable(opt.value) || readonly }">
          <input type="radio" :value="opt.value" :checked="opt.value === creativeType"
            :disabled="readonly || !isCreativeAvailable(opt.value)"
            @change="$emit('update-types', { creative_type: opt.value })" />
          <img v-if="opt.icon" :src="opt.icon" :alt="opt.label" class="creative-icon" />
          <span>{{ opt.label }}</span>
        </label>
      </div>
    </div>

    <div class="field-group-item">
      <span>{{ inventoryLabel }}</span>
      <div class="env-checkboxes">
        <label v-for="e in environments" :key="e.value" class="env-item"
          :class="{ disabled: !isEnvironmentAvailable(e.value) || readonly }">
          <input type="checkbox" :value="e.value" :checked="selectedEnvs.includes(e.value)"
            :disabled="readonly || !isEnvironmentAvailable(e.value)"
            @change="toggleEnv(e.value, $event.target.checked)" />
          <span>{{ e.label }}</span>
        </label>
      </div>
    </div>

    <div class="field-group-item">
      <span>{{ formatLabel }}</span>
      <select :value="adFormat" @change="$emit('update-types', { ad_format: $event.target.value })" class="form-select"
        :disabled="readonly">
        <option value="">{{ $t('messages.select_placeholder') }}</option>
        <option v-for="f in adFormats" :key="f" :value="f" :disabled="!isAdFormatAvailable(f)">{{ f }}</option>
      </select>
    </div>

    <div class="field-group-item">
      <span>{{ paymentLabel }}</span>
      <div class="payment-radios">
        <label v-for="m in models" :key="m" class="payment-item" :class="{ disabled: !isKpiAvailable(m) || readonly }">
          <input type="radio" name="paymentModel" :value="m" :checked="paymentModel === m"
            :disabled="readonly || !isKpiAvailable(m)" @change="onKpiChange(m)" />
          <span>{{ m }}</span>
        </label>
      </div>
    </div>

    <div class="budget-row">
      <div class="budget-input">
        <span>{{ $t('messages.total_budget') }}</span>
        <input type="number" step="0.01" min="0" v-model.number="localBudget" class="form-input" @input="onBudgetInput"
          :disabled="readonly" />
      </div>
      <div>
        <label>{{ $t('messages.price') }}</label>
        <div class="form-input" style="background:#f7f7f7;">{{ priceDisplay }}</div>
      </div>
    </div>

    <div class="budget-row volume-row">
      <div class="budget-input">
        <span>{{ plannedVolumeLabel }}</span>
        <input type="number" step="1" min="0" v-model.number="localPlannedVolume" class="form-input"
          @input="onVolumeInput" :disabled="readonly" />
      </div>
    </div>
  </div>
</template>

<script>
import { suggestPlannedVolume, volumeUnitLabel } from '../../../../../utils/plannedVolume';

export default {
  name: "AdGroupTypes",
  props: {
    creativeType: { type: String, default: "" },
    environment: { type: String, default: "" },
    adFormat: { type: String, default: "" },
    priceDisplay: { type: String, default: "—" },
    priceValue: { type: [Number, String], default: null },
    totalBudget: { type: [Number, String], default: null },
    plannedVolume: { type: [Number, String], default: null },
    creativeTypeOptions: { type: Array, default: () => [] },
    environments: { type: Array, default: () => [] },
    adFormats: { type: Array, default: () => [] },
    availableCreativeTypes: { type: Array, default: () => [] },
    availableEnvironments: { type: Array, default: () => [] },
    availableAdFormats: { type: Array, default: () => [] },
    availableKpis: { type: Array, default: () => [] },
    typesLabel: { type: String, default: "Тип рекламы" },
    inventoryLabel: { type: String, default: "Тип инвентаря" },
    formatLabel: { type: String, default: "Формат рекламы" },
    paymentLabel: { type: String, default: "Модель оплаты" },
    paymentModel: { type: String, default: "" },
    readonly: { type: Boolean, default: false }
  },
  emits: ['update-types'],
  data() {
    return {
      models: ['CPM', 'CPC', 'CPV', 'CPE'],
      localBudget: null,
      localPlannedVolume: null,
      volumeTouched: false
    };
  },
  computed: {
    selectedEnvs() {
      if (!this.environment) return [];
      return String(this.environment).split(';').filter(Boolean);
    },
    priceNumber() {
      const v = this.priceValue;
      if (v === null || v === '' || isNaN(Number(v))) return null;
      return Number(v);
    },
    budgetNumber() {
      const b = this.localBudget;
      if (b === null || b === '' || isNaN(Number(b))) return null;
      return Number(b);
    },
    plannedVolumeLabel() {
      const unit = volumeUnitLabel(this.paymentModel, (key) => this.$t(key));
      return `${this.$t('messages.planned_placement_volume')} (${unit})`;
    }
  },
  watch: {
    totalBudget(val) {
      this.localBudget = val;
    },
    plannedVolume(val) {
      if (!this.volumeTouched) {
        this.localPlannedVolume = val === null || val === '' ? null : Number(val);
      }
    },
    priceValue() {
      this.maybeSuggestVolume();
    },
    paymentModel() {
      this.maybeSuggestVolume();
    }
  },
  mounted() {
    this.localBudget = this.totalBudget;
    this.localPlannedVolume = this.plannedVolume === null || this.plannedVolume === '' ? null : Number(this.plannedVolume);
  },
  methods: {
    isCreativeAvailable(value) {
      return (this.availableCreativeTypes.length === 0) || this.availableCreativeTypes.includes(value);
    },
    isEnvironmentAvailable(value) {
      return (this.availableEnvironments.length === 0) || this.availableEnvironments.includes(value);
    },
    isAdFormatAvailable(value) {
      return (this.availableAdFormats.length === 0) || this.availableAdFormats.includes(value);
    },
    isKpiAvailable(value) {
      return (this.availableKpis.length === 0) || this.availableKpis.includes(value);
    },
    maybeSuggestVolume() {
      if (this.volumeTouched) return;
      const suggested = suggestPlannedVolume(this.paymentModel, this.budgetNumber, this.priceNumber);
      if (suggested === null) return;
      this.localPlannedVolume = suggested;
      this.$emit('update-types', { planned_volume: suggested });
    },
    onBudgetInput() {
      const val = (this.localBudget === '' || this.localBudget === null) ? null : Number(this.localBudget);
      this.$emit('update-types', { total_budget: val });
      this.maybeSuggestVolume();
    },
    onVolumeInput() {
      this.volumeTouched = true;
      const val = (this.localPlannedVolume === '' || this.localPlannedVolume === null) ? null : Number(this.localPlannedVolume);
      this.$emit('update-types', { planned_volume: val });
    },
    onKpiChange(kpi) {
      this.volumeTouched = false;
      this.$emit('update-types', { kpi });
      this.$nextTick(() => this.maybeSuggestVolume());
    },
    toggleEnv(value, checked) {
      const arr = this.selectedEnvs.slice();
      const idx = arr.indexOf(value);
      if (checked && idx === -1) {
        arr.push(value);
      } else if (!checked && idx !== -1) {
        arr.splice(idx, 1);
      }
      arr.sort();
      this.$emit('update-types', { environment: arr.join(';') || '' });
    }
  }
};
</script>

<style lang="scss" scoped>
.field-group {
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
  gap: 30px;

  .field-group-item {
    margin-bottom: 20px;
    display: flex;
    flex-direction: column;
    gap: 6px;

    .creative-type-list {
      display: flex;
      gap: 30px;
      align-items: center;

      .creative-type-item {
        display: flex;
        align-items: center;
        gap: 3px;
        cursor: pointer;

        &.disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }
      }
    }
  }
}

.creative-icon {
  width: 20px;
}

.env-checkboxes {
  display: flex;
  gap: 25px;
  align-items: center;
}

.payment-radios {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.field-group input[type="radio"],
.field-group input[type="checkbox"] {
  display: inline-block !important;
  width: 16px;
  height: 16px;
  margin-right: 6px;
  vertical-align: middle;
}

.budget-row {
  display: flex;
  gap: 12px;
  align-items: center;
}

.budget-input {
  flex: 0 0 50%;
}

.volume-row {
  margin-top: -12px;
}
</style>
